import { useState, useEffect, useRef, useCallback } from "react"
import { GetHelpCenterTOC, GetArticle, GetArticleInCategory } from "../api"
import { usePromise } from "aldoo-ra/usePromise"
import { getHelpCenterBase, parseHelpCenterPath } from "../urlUtils"
import { findArticleInToc as findArticleInTocData, findCategoryInToc as findCategoryInTocData } from "../toc"
import Value from "aldoo-ra/Value"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { usePageContext } from "vike-react/usePageContext"

// The page as the server rendered it (../ssr.js, `helpCenterSSR`): used on
// the server and on the first client render while it is still the same
// page in the same language, so the content is in the HTML and hydrates as is
function useSSRSeed(currentLanguage) {
  let pageContext = null
  try {
    pageContext = usePageContext()
  } catch {
    // outside a Vike page: no server copy
  }
  const seed = useRef(undefined)
  if (seed.current === undefined) {
    const ssr = pageContext?.helpCenterSSR
    const samePage =
      typeof window === "undefined" ||
      (window.location.pathname === ssr?.pathname && (ssr?.language || "en") === currentLanguage)
    seed.current = ssr?.toc && samePage ? ssr : null
  }
  return seed.current
}

// What the content loader shows for a server copy: the breadcrumbs and
// sidebar expansion loadArticle / loadCategory would set
function seededState(seed, homeLabel) {
  const home = { label: homeLabel, link: getHelpCenterBase(seed?.pathname) }
  if (!seed?.content) return { breadcrumbs: [home], expanded: {} }
  const breadcrumbs = [home, ...(seed.breadcrumbData || [])]
  const name = seed.content.content?.name
  if (seed.contentType === "article" && breadcrumbs[breadcrumbs.length - 1].label !== name) breadcrumbs.push({ label: name })
  const expanded = {}
  let cumulative = ""
  for (const slug of (seed.categoryPath || "").split("/").filter(Boolean)) {
    cumulative = cumulative ? `${cumulative}/${slug}` : slug
    expanded[cumulative] = true
    expanded[slug] = true
  }
  return { breadcrumbs, expanded }
}

function useContentLoader() {
  const texts = TextBundle({ id: "help-center" })

  const [currentLanguage] = Value({
    key: "current-site-language",
    defaultValue: "en",
    persistKey: "current-site-language",
  })

  const lastLoadedLanguage = useRef(currentLanguage)

  const seed = useSSRSeed(currentLanguage)
  const [seeded] = useState(() => seededState(seed, texts?.help_center || "Help Center"))

  const [selectedContent, setSelectedContent] = useState(seed?.content || null)
  const [contentType, setContentType] = useState(seed?.contentType || null)
  const [error, setError] = useState(null)
  const [rootCategories, setRootCategories] = useState(seed?.toc?.content?.categories || null)
  const [expandedCategories, setExpandedCategories] = useState(seeded.expanded)
  const [activeContentSlug, setActiveContentSlug] = useState(
    seed?.contentType === "article" ? seed.content?.content?.slug || null : seed?.categoryPath || null
  )
  const [activeContentType, setActiveContentType] = useState(seed?.contentType || null)
  const [breadcrumbItems, setBreadcrumbItems] = useState(seeded.breadcrumbs)
  const [browserInitialized, setBrowserInitialized] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)
  const initRef = useRef(false)
  const refreshPendingRef = useRef(false)
  const loadContentFromUrlRef = useRef(null)

  const activeContentRef = useRef({ type: null, slug: null, categoryPath: null })

  const getTocPromise = useCallback(
    async (language = currentLanguage) => {
      const response = await GetHelpCenterTOC(language)
      if (response && response.success && response.toc) return response.toc
      throw new Error(texts?.toc_load_error || "Failed to load help center structure")
    },
    [currentLanguage]
  )

  const {
    result: loadedToc,
    pending: isTocLoading,
    error: tocError,
    execute: executeTocLoad,
    refresh: refreshToc,
  } = usePromise(getTocPromise, true)

  // the server's copy stands until the browser has loaded its own
  const tocData = loadedToc || seed?.toc
  const isLoading = Boolean(isTocLoading) && !tocData
  const combinedError = error || (tocError instanceof Error ? tocError.message : tocError) || null

  const parseUrlPath = useCallback(() => {
    if (typeof window === "undefined")
      return { type: null, categoryPath: null, articleSlug: null }

    const parsed = parseHelpCenterPath(window.location.pathname)
    if (!parsed.type && /\/help-center\/?$/.test(window.location.pathname)) setExpandedCategories({})
    return parsed
  }, [setExpandedCategories])

  const findArticleInToc = useCallback(
    (slug, categoryPath = null) => findArticleInTocData(tocData, slug, categoryPath),
    [tocData]
  )

  const findCategoryInToc = useCallback(
    (categoryPath) => findCategoryInTocData(tocData, categoryPath),
    [tocData]
  )

  const buildExpansionMap = useCallback(
    (categoryPath) => {
      if (!categoryPath) return {}

      const pathParts = categoryPath.split("/")
      const newExpandedCategories = { ...expandedCategories }
      let cumulativePath = ""

      for (const slug of pathParts) {
        cumulativePath = cumulativePath ? `${cumulativePath}/${slug}` : slug
        newExpandedCategories[cumulativePath] = true
        newExpandedCategories[slug] = true
      }

      return newExpandedCategories
    },
    [expandedCategories]
  )

  const loadArticle = useCallback(
    async (slug, categoryPath = null) => {
      if (!slug || !tocData) return null

      setError(null)

      try {
        activeContentRef.current = { type: "article", slug, categoryPath }

        // Try exact path first, fall back to recursive tree search
        let result = findArticleInToc(slug, categoryPath)
        if (!result) result = findArticleInToc(slug, null)

        // Derive the full tree path from TOC breadcrumbs for sidebar expansion
        const fullCategoryPath = result?.breadcrumbData
          ?.filter((b) => b.categorySlug)
          .map((b) => b.categorySlug)
          .join("/") || categoryPath

        if (fullCategoryPath) {
          setExpandedCategories(buildExpansionMap(fullCategoryPath))

          // If the derived path differs from the provided one, update the URL so the sidebar can match
          if (fullCategoryPath !== categoryPath && typeof window !== "undefined") {
            const newUrl = `${getHelpCenterBase()}/category/${fullCategoryPath}/article/${slug}`
            if (window.location.pathname !== newUrl) {
              window.skipNextHelpCenterUrlChange = true
              window.history.replaceState(null, "", newUrl)
            }
          }
        }

        const articleData = fullCategoryPath
          ? await GetArticleInCategory(fullCategoryPath, slug)
          : await GetArticle(slug)

        if (!articleData) throw new Error(`Article not found: ${slug}`)

        // Build breadcrumbs from TOC result or fall back to URL path
        let breadcrumbData = result?.breadcrumbData || []
        if (!breadcrumbData.length && categoryPath) {
          breadcrumbData = categoryPath.split("/").map((part, i, arr) => ({
            label: part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, " "),
            link: i < arr.length - 1 ? `/help-center/category/${arr.slice(0, i + 1).join("/")}` : undefined,
          })).filter(b => b.link)
        }

        const newBreadcrumbs = [
          { label: texts?.help_center || "Help Center", link: getHelpCenterBase() },
          ...breadcrumbData,
        ]

        if (
          newBreadcrumbs[newBreadcrumbs.length - 1].label !==
          articleData.content.name
        ) {
          newBreadcrumbs.push({ label: articleData.content.name })
        }

        setBreadcrumbItems(newBreadcrumbs)
        setSelectedContent(articleData)
        setContentType("article")
        setActiveContentSlug(slug)
        setActiveContentType("article")
        lastLoadedLanguage.current = currentLanguage

        return articleData
      } catch (err) {
        console.error("loadArticle error:", err)
        setError(texts?.article_load_error || "Failed to load article. Please try again later.")
        return null
      }
    },
    [buildExpansionMap, currentLanguage, findArticleInToc, tocData]
  )

  const loadCategory = useCallback(
    async (categoryPath) => {
      if (!categoryPath || !tocData) return null

      setError(null)

      try {
        activeContentRef.current = { type: "category", slug: categoryPath, categoryPath: null }

        if (categoryPath.includes("/")) {
          setExpandedCategories(buildExpansionMap(categoryPath))
        }

        const result = findCategoryInToc(categoryPath)
        if (!result) throw new Error(`Category not found: ${categoryPath}`)

        const { categoryContent, breadcrumbData } = result

        const newBreadcrumbs = [
          { label: texts?.help_center || "Help Center", link: getHelpCenterBase() },
          ...breadcrumbData,
        ]

        setBreadcrumbItems(newBreadcrumbs)
        setSelectedContent(categoryContent)
        setContentType("category")
        setActiveContentSlug(categoryPath)
        setActiveContentType("category")
        lastLoadedLanguage.current = currentLanguage

        return categoryContent
      } catch (err) {
        setError(texts?.category_load_error || "Failed to load category. Please try again later.")
        return null
      }
    },
    [buildExpansionMap, currentLanguage, findCategoryInToc, tocData]
  )

  const loadContentFromUrl = useCallback(async () => {
    if (!tocData) return

    setError(null)

    try {
      const urlParams = parseUrlPath()

      if (!urlParams.type && !urlParams.categoryPath && !urlParams.articleSlug) {
        setExpandedCategories({})
        setSelectedContent(null)
        setContentType(null)
        setBreadcrumbItems([{ label: texts?.help_center || "Help Center", link: getHelpCenterBase() }])
        setActiveContentSlug(null)
        setActiveContentType(null)
        activeContentRef.current = { type: null, slug: null, categoryPath: null }
      } else if (urlParams.categoryPath) {
        setExpandedCategories(buildExpansionMap(urlParams.categoryPath))
      }

      if (urlParams.type === "article") {
        await loadArticle(urlParams.articleSlug, urlParams.categoryPath)
      } else if (urlParams.type === "category") {
        await loadCategory(urlParams.categoryPath)
      }

      lastLoadedLanguage.current = currentLanguage
    } catch (err) {
      setError(texts?.content_load_error || "Failed to load content. Please try again later.")
    }
  }, [
    parseUrlPath,
    buildExpansionMap,
    loadArticle,
    loadCategory,
    currentLanguage,
    tocData,
  ])

  const refreshContent = useCallback(async () => {
    setError(null)

    try {
      const currentPath = window.location.pathname
      const isHomePage = /\/help-center\/?$/.test(currentPath)

      if (isHomePage) {
        setExpandedCategories({})
        setActiveContentSlug(null)
        setActiveContentType(null)
        setBreadcrumbItems([{ label: texts?.help_center || "Help Center", link: getHelpCenterBase() }])
        setSelectedContent(null)
        setContentType(null)
        activeContentRef.current = { type: null, slug: null, categoryPath: null }
      } else {
        refreshPendingRef.current = true
      }

      const loadMethod = refreshToc || executeTocLoad || GetHelpCenterTOC
      loadMethod(currentLanguage)
      lastLoadedLanguage.current = currentLanguage
    } catch (err) {
      setError(`${texts?.refresh_error_prefix || "Failed to refresh content"}: ${err.message}`)
    }
  }, [currentLanguage, refreshToc, executeTocLoad])

  const updateUrl = useCallback((type, slug, categoryPath = "") => {
    if ((type === "article" || type === "category") && !slug) return

    let url = getHelpCenterBase()

    if (type === "article") {
      url += categoryPath
        ? `/category/${categoryPath}/article/${slug}`
        : `/article/${slug}`
    } else if (type === "category") {
      url += `/category/${categoryPath ? `${categoryPath}/` : ""}${slug}`
    }

    if (window.location.pathname !== url) {
      window.skipNextHelpCenterUrlChange = true
      window.history.pushState({ type, slug, categoryPath }, "", url)
      window.dispatchEvent(new CustomEvent("helpCenterNavigate"))
    }
  }, [])

  const expandCategoryPath = useCallback(
    (path) => {
      if (!path) return
      setExpandedCategories(
        buildExpansionMap(typeof path === "string" ? path : path.join("/"))
      )
    },
    [buildExpansionMap]
  )

  const loadToc = useCallback(() => {
    executeTocLoad(currentLanguage)
  }, [executeTocLoad, currentLanguage])

  // Hydration
  useEffect(() => { setIsHydrated(true) }, [])

  // Initial TOC load
  useEffect(() => {
    if (!tocData && !isTocLoading) executeTocLoad(currentLanguage)
  }, [])

  // Home page state init
  useEffect(() => {
    if (!isHydrated || typeof window === "undefined") return
    const path = window.location.pathname
    const isHelpCenterHome = /\/help-center\/?$/.test(path)

    if (isHelpCenterHome && !selectedContent && !contentType) {
      setSelectedContent(null)
      setContentType(null)
      setBreadcrumbItems([{ label: texts?.help_center || "Help Center", link: getHelpCenterBase() }])
      setActiveContentSlug(null)
      setActiveContentType(null)
      setExpandedCategories({})
      activeContentRef.current = { type: null, slug: null, categoryPath: null }
    }
  }, [isHydrated, selectedContent, contentType])

  // Sync root categories from TOC
  useEffect(() => {
    if (tocData && tocData.content && tocData.content.categories) {
      setRootCategories(tocData.content.categories)
    }
  }, [tocData])

  // Keep ref current so the tocData effect always calls the latest version
  // without needing loadContentFromUrl in its dependency array
  useEffect(() => {
    loadContentFromUrlRef.current = loadContentFromUrl
  }, [loadContentFromUrl])

  // Initialize content from URL once TOC loads; also reload after language refresh.
  // Depends only on tocData so it fires only when new TOC data arrives,
  // not when loadContentFromUrl is recreated due to a currentLanguage change
  // (which would prematurely consume refreshPendingRef before the new TOC is ready).
  useEffect(() => {
    if (!tocData) return

    if (!initRef.current) {
      initRef.current = true
      const currentUrl = typeof window !== "undefined" ? window.location.pathname : ""
      if (currentUrl === "/help-center/submit-request") {
        setBrowserInitialized(true)
        return
      }
      loadContentFromUrlRef.current().then(() => setBrowserInitialized(true))
      return
    }

    if (refreshPendingRef.current) {
      refreshPendingRef.current = false
      const path = typeof window !== "undefined" ? window.location.pathname : ""
      if (!path.includes("/submit-request")) loadContentFromUrlRef.current()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tocData])

  // URL change monitor
  useEffect(() => {
    if (!isHydrated || !tocData) return

    let lastUrl = window.location.pathname
    let isProcessingUrlChange = false
    let debounceTimer = null

    const handleUrlChange = async () => {
      if (isProcessingUrlChange) return
      if (debounceTimer) { clearTimeout(debounceTimer); debounceTimer = null }
      if (window.skipNextHelpCenterUrlChange) {
        window.skipNextHelpCenterUrlChange = false
        return
      }

      const currentUrl = window.location.pathname
      if (
        !currentUrl.includes("/help-center") ||
        currentUrl.includes("/help-center/submit-request")
      ) return

      isProcessingUrlChange = true

      try {
        const shouldBeHomePage = /\/help-center\/?$/.test(currentUrl)

        if (shouldBeHomePage) {
          setSelectedContent(null)
          setContentType(null)
          setBreadcrumbItems([{ label: texts?.help_center || "Help Center", link: getHelpCenterBase() }])
          setActiveContentSlug(null)
          setActiveContentType(null)
          setExpandedCategories({})
          activeContentRef.current = { type: null, slug: null, categoryPath: null }
        } else {
          await loadContentFromUrl()
        }
      } catch (error) {
        setError(texts?.url_content_load_error || "Failed to load content")
      } finally {
        isProcessingUrlChange = false
      }
    }

    const urlMonitor = setInterval(() => {
      const currentUrl = window.location.pathname
      if (
        currentUrl.includes("/help-center") &&
        !currentUrl.includes("/help-center/submit-request") &&
        lastUrl !== currentUrl
      ) {
        lastUrl = currentUrl
        if (debounceTimer) clearTimeout(debounceTimer)
        debounceTimer = setTimeout(handleUrlChange, 100)
      }
    }, 1000)

    const handlePopState = () => {
      const currentUrl = window.location.pathname
      if (
        currentUrl.includes("/help-center") &&
        !currentUrl.includes("/help-center/submit-request")
      ) {
        lastUrl = currentUrl
        setTimeout(handleUrlChange, 50)
      }
    }

    window.addEventListener("popstate", handlePopState)

    return () => {
      clearInterval(urlMonitor)
      if (debounceTimer) clearTimeout(debounceTimer)
      window.removeEventListener("popstate", handlePopState)
    }
  }, [isHydrated, tocData, loadContentFromUrl])

  return {
    selectedContent,
    contentType,
    isLoading,
    error: combinedError,
    tocData,
    rootCategories,
    expandedCategories,
    activeContentSlug,
    activeContentType,
    breadcrumbItems,
    browserInitialized,
    categoriesLoaded: !!tocData,
    currentLanguage,
    setExpandedCategories,
    setActiveContentSlug,
    setActiveContentType,
    setBreadcrumbItems,
    setSelectedContent,
    setContentType,
    setError,
    loadArticle,
    loadCategory,
    parseUrlPath,
    buildExpansionMap,
    updateUrl,
    loadContentFromUrl,
    expandCategoryPath,
    loadToc,
    refreshContent,
  }
}

export default useContentLoader
